"use client";

import * as React from "react";

type Attribute = `data-${string}` | "class";

interface ThemeProviderProps extends React.PropsWithChildren {
  themes?: string[];
  forcedTheme?: string;
  enableSystem?: boolean;
  disableTransitionOnChange?: boolean;
  enableColorScheme?: boolean;
  storageKey?: string;
  defaultTheme?: string;
  attribute?: Attribute | Attribute[];
  value?: Record<string, string>;
  nonce?: string;
}

interface UseThemeProps {
  themes: string[];
  forcedTheme?: string;
  setTheme: React.Dispatch<React.SetStateAction<string>>;
  theme?: string;
  resolvedTheme?: string;
  systemTheme?: "dark" | "light";
}

const colorSchemes = ["light", "dark"];
const MEDIA = "(prefers-color-scheme: dark)";
const ThemeContext = React.createContext<UseThemeProps | undefined>(undefined);
const defaultContext: UseThemeProps = { setTheme: () => {}, themes: [] };

const getSystemTheme = (e?: MediaQueryList | MediaQueryListEvent) => {
  const media = e ?? window.matchMedia(MEDIA);
  return media.matches ? "dark" : "light";
};

const getStoredTheme = (key: string, fallback?: string) => {
  if (typeof window === "undefined") return undefined;
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

const disableAnimation = (nonce?: string) => {
  const css = document.createElement("style");
  if (nonce) css.setAttribute("nonce", nonce);
  css.appendChild(
    document.createTextNode(
      "*,*::before,*::after{-webkit-transition:none!important;-moz-transition:none!important;-o-transition:none!important;-ms-transition:none!important;transition:none!important}",
    ),
  );
  document.head.appendChild(css);
  return () => {
    (() => window.getComputedStyle(document.body))();
    setTimeout(() => document.head.removeChild(css), 1);
  };
};

export function useTheme() {
  return React.useContext(ThemeContext) ?? defaultContext;
}

export function ThemeProvider(props: ThemeProviderProps) {
  const context = React.useContext(ThemeContext);
  if (context) return <>{props.children}</>;
  return <Theme {...props} />;
}

const defaultThemes = ["light", "dark"];

function Theme({
  forcedTheme,
  disableTransitionOnChange = false,
  enableSystem = true,
  enableColorScheme = true,
  storageKey = "theme",
  themes = defaultThemes,
  defaultTheme = enableSystem ? "system" : "light",
  attribute = "data-theme",
  value,
  children,
  nonce,
}: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState(() => getStoredTheme(storageKey, defaultTheme));
  const [resolvedTheme, setResolvedTheme] = React.useState(() =>
    theme === "system" ? getSystemTheme() : theme,
  );
  const attrs = !value ? themes : Object.values(value);

  const applyTheme = React.useCallback(
    (theme: string | undefined) => {
      let resolved = theme;
      if (!resolved) return;
      if (theme === "system" && enableSystem) {
        resolved = getSystemTheme();
      }
      const name = value ? value[resolved] : resolved;
      const enable = disableTransitionOnChange ? disableAnimation(nonce) : null;
      const d = document.documentElement;

      const handleAttribute = (attr: Attribute) => {
        if (attr === "class") {
          d.classList.remove(...attrs);
          if (name) d.classList.add(name);
        } else if (attr.startsWith("data-")) {
          if (name) d.setAttribute(attr, name);
          else d.removeAttribute(attr);
        }
      };

      if (Array.isArray(attribute)) attribute.forEach(handleAttribute);
      else handleAttribute(attribute);

      if (enableColorScheme) {
        const fallback = colorSchemes.includes(defaultTheme) ? defaultTheme : null;
        const colorScheme = colorSchemes.includes(resolved) ? resolved : fallback;
        if (colorScheme) d.style.colorScheme = colorScheme;
      }

      enable?.();
    },
    [nonce],
  );

  const setTheme = React.useCallback((value: string | ((prev: string) => string)) => {
    const newTheme = typeof value === "function" ? value(theme!) : value;
    setThemeState(newTheme);
    try {
      localStorage.setItem(storageKey, newTheme);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const handleMediaQuery = React.useCallback(
    (e: MediaQueryListEvent | MediaQueryList) => {
      const resolved = getSystemTheme(e);
      setResolvedTheme(resolved);
      if (theme === "system" && enableSystem && !forcedTheme) {
        applyTheme("system");
      }
    },
    [theme, forcedTheme],
  );

  React.useEffect(() => {
    const media = window.matchMedia(MEDIA);
    media.addListener(handleMediaQuery);
    handleMediaQuery(media);
    return () => media.removeListener(handleMediaQuery);
  }, [handleMediaQuery]);

  React.useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== storageKey) return;
      if (!e.newValue) {
        setThemeState(defaultTheme);
      } else {
        setThemeState(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [setThemeState, storageKey, defaultTheme]);

  React.useEffect(() => {
    applyTheme(forcedTheme ?? theme);
  }, [forcedTheme, theme]);

  const providerValue = React.useMemo(
    () => ({
      theme,
      setTheme,
      forcedTheme,
      resolvedTheme: theme === "system" ? resolvedTheme : theme,
      themes: enableSystem ? [...themes, "system"] : themes,
      systemTheme: (enableSystem ? resolvedTheme : undefined) as "dark" | "light" | undefined,
    }),
    [theme, setTheme, forcedTheme, resolvedTheme, enableSystem, themes],
  );

  return <ThemeContext.Provider value={providerValue}>{children}</ThemeContext.Provider>;
}

export type { Attribute, ThemeProviderProps, UseThemeProps };
